import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Food Security Gap", href: "/#gap" },
  { label: "Our System", href: "/#system" },
  { label: "Orchards", href: "/orchards", route: true },
  { label: "Impact", href: "/#impact" },
  { label: "Policy Briefing", href: "/policy-briefing", route: true },
  { label: "Contact", href: "/#contact" },
];

export default function Navigation({ scrolled }) {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-sand/85 backdrop-blur-xl border-b border-stone-line/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`flex items-center gap-3 ${
            scrolled ? "text-charcoal" : "text-sand"
          } transition-colors`}
        >
          <span className="inline-block w-2 h-2 bg-rust" aria-hidden />
          <span className="font-serif text-xl md:text-2xl tracking-tight leading-none">
            Growing<span className="italic font-light"> Country</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => {
            const cls = `text-[11px] uppercase tracking-[0.22em] ${
              scrolled
                ? "text-charcoal/80 hover:text-rust"
                : "text-sand/85 hover:text-sand"
            } transition-colors`;
            const testid = `nav-link-${l.label
              .toLowerCase()
              .replace(/[\s']+/g, "-")}`;
            return l.route ? (
              <Link key={l.href} to={l.href} className={cls} data-testid={testid}>
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} className={cls} data-testid={testid}>
                {l.label}
              </a>
            );
          })}
        </nav>

        <a
          href="/#get-involved"
          data-testid="nav-cta-partner"
          className={`hidden lg:inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.22em] transition-colors ${
            scrolled
              ? "bg-rust text-sand hover:bg-rust-dark"
              : "bg-sand text-charcoal hover:bg-stone-alt"
          }`}
        >
          Partner with us
        </a>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 -mr-2 ${scrolled ? "text-charcoal" : "text-sand"}`}
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-sand border-t border-stone-line/60"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => {
              const cls =
                "text-sm uppercase tracking-[0.22em] text-charcoal/80 hover:text-rust";
              const testid = `mobile-link-${l.label
                .toLowerCase()
                .replace(/[\s']+/g, "-")}`;
              return l.route ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className={cls}
                  data-testid={testid}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={cls}
                  data-testid={testid}
                >
                  {l.label}
                </a>
              );
            })}
            <a
              href="/#get-involved"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-6 py-4 bg-rust text-sand text-xs uppercase tracking-[0.22em]"
              data-testid="mobile-cta-partner"
            >
              Partner with us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
